import type { ClinicalSeverityData } from '@/lib/clinicalSeverityTypes';
import type { CaseCptLine, Laterality, ScheduledCase, SurgeonTemplate } from '@/lib/templates/types';
import {
  clinicalSeverityEvidenceText,
  hasAscExclusionSiteOfCareJustification,
  hasKneeMuaFunctionalLimitation,
  hasKneeMuaRomEvidence,
  hasKneeMuaTherapyCourse,
  hasShoulderFormalTherapyFailure,
} from './evidenceValidation';

export type ProofStatus = 'satisfied' | 'needs_attestation' | 'missing' | 'not_applicable';
export type ConversationMode = 'pre-auth' | 'post-op';

export interface ProofCriterion {
  id: string;
  label: string;
  status: ProofStatus;
  explanation: string;
  source?: string;
}

export interface LiveValidationRequirement {
  id: string;
  label: string;
  keywordGroups: string[][];
}

export interface ClinicalQuestion {
  id: string;
  mode: ConversationMode;
  title: string;
  spokenPrompt: string;
  payerCriterion: string;
  missingFact: string;
  suggestedLanguage: string;
  answerPlaceholder: string;
  requirements: LiveValidationRequirement[];
  severity: 'clinical' | 'revenue' | 'rcm';
  allowUseVerbatim: boolean;
}

export interface RevenueOpportunity {
  id: string;
  title: string;
  reason: string;
  suggestedLanguage: string;
  estimatedImpactLabel: string;
  requiresSurgeonAttestation: boolean;
}

export interface SurgeonConsoleProofPacket {
  mode: ConversationMode;
  patientName: string;
  procedureName: string;
  cptCodes: string[];
  readinessStatus: 'ready' | 'needs_surgeon' | 'needs_staff' | 'blocked';
  summary: string;
  criteria: ProofCriterion[];
  questions: ClinicalQuestion[];
  revenueOpportunities: RevenueOpportunity[];
}

export interface ConversationValidationResult {
  requirementId: string;
  label: string;
  satisfied: boolean;
}

function normalize(text: string | null | undefined): string {
  return (text || '').toLowerCase();
}

function containsAny(text: string, keywords: string[]): boolean {
  return keywords.some(keyword => text.includes(keyword.toLowerCase()));
}

function cptCode(line: Pick<CaseCptLine, 'code'>): string {
  return String(line.code);
}

function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return new Date(Date.UTC(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3])));
  }
  const usMatch = value.match(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/);
  if (usMatch) {
    return new Date(Date.UTC(Number(usMatch[3]), Number(usMatch[1]) - 1, Number(usMatch[2])));
  }
  return null;
}

function daysBetween(start: Date, end: Date): number {
  return Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}

function formatDate(date: Date): string {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}-${day}`;
}

function criterion(
  id: string,
  label: string,
  passed: boolean,
  explanation: string,
  source?: string,
): ProofCriterion {
  return {
    id,
    label,
    status: passed ? 'satisfied' : 'needs_attestation',
    explanation,
    source,
  };
}

function hasModifier(cptLines: CaseCptLine[], modifier: string): boolean {
  return cptLines.some(line => line.modifiers.includes(modifier));
}

function primaryLateralityModifier(laterality: Laterality | null): string | null {
  if (laterality === 'left') return 'LT';
  if (laterality === 'right') return 'RT';
  if (laterality === 'bilateral') return '50';
  return null;
}

function getPacketText(
  selectedCase: ScheduledCase,
  clinicalText?: string | null,
  clinicalSeverityData?: ClinicalSeverityData | null,
): string {
  const docText = (selectedCase.pre_loaded_docs || [])
    .map(doc => [doc.type, doc.extracted_text].filter(Boolean).join(': '))
    .join('\n\n');
  return [clinicalText, clinicalSeverityEvidenceText(clinicalSeverityData), selectedCase.scheduled_procedure, selectedCase.payer_name, docText]
    .filter(Boolean)
    .join('\n\n');
}

function getPriorTkaDate(params: {
  selectedCase: ScheduledCase;
  template: SurgeonTemplate;
  text: string;
}): Date | null {
  const metadataDate = typeof params.template.metadata?.prior_tka_date === 'string'
    ? params.template.metadata.prior_tka_date
    : null;
  const fromMetadata = parseDateOnly(metadataDate);
  if (fromMetadata) return fromMetadata;
  const tkaMatch = params.text.match(/\b(?:tka|total knee (?:replacement|arthroplasty))\D{0,80}(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})/i);
  return parseDateOnly(tkaMatch?.[1]);
}

function getMuaGlobalAssessment(params: {
  selectedCase: ScheduledCase;
  template: SurgeonTemplate;
  text: string;
}): { daysElapsed: number; globalEndsOn: Date; insideGlobal: boolean } | null {
  const priorTkaDate = getPriorTkaDate(params);
  const surgeryDate = parseDateOnly(params.selectedCase.scheduled_date);
  if (!priorTkaDate || !surgeryDate) return null;
  const globalDays = typeof params.template.metadata?.global_period_days === 'number'
    ? params.template.metadata.global_period_days
    : 90;
  const daysElapsed = daysBetween(priorTkaDate, surgeryDate);
  return {
    daysElapsed,
    globalEndsOn: addDays(priorTkaDate, globalDays),
    insideGlobal: daysElapsed <= globalDays,
  };
}

function hasMuaFunctionalDeficit(text: string): boolean {
  return /(reciprocal\s+stair|stair\s+climb|stairs|rising\s+from|rise\s+from|standard\s+seated|chair|without\s+assistance|mechanical\s+block)/i.test(text);
}

function hasFormalPtTimeline(text: string): boolean {
  const datePattern = String.raw`(?:\d{1,2}/\d{1,2}/\d{4}|\d{4}-\d{2}-\d{2})`;
  return new RegExp(String.raw`(?:formal|supervised|in[- ]person)[^.\n]{0,160}${datePattern}[^.\n]{0,160}${datePattern}`, 'i').test(text) ||
    new RegExp(String.raw`${datePattern}[^.\n]{0,180}${datePattern}[^.\n]{0,120}(?:formal|supervised|therapy|pt)`, 'i').test(text);
}

function hasStaticRomTrendline(text: string): boolean {
  const dateMatches = text.match(/\b(?:\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})\b/g) || [];
  return /(95\s*(?:to|-)\s*100|0\s*(?:to|-)\s*100|100\s*degrees)/i.test(text) &&
    /(consecutive|static|plateau|measurements?)/i.test(text) &&
    dateMatches.length >= 3;
}

function therapyWindow(text: string): { start: Date; end: Date; elapsedDays: number; clinicalWeek: number } | null {
  const datePattern = String.raw`(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})`;
  const match = text.match(new RegExp(String.raw`(?:therapy|pt|rehabilitation|physical therapy)[^.\n]{0,180}(?:from|between)?\s*${datePattern}[^.\n]{0,60}(?:through|to|-|and)\s*${datePattern}`, 'i')) ||
    text.match(new RegExp(String.raw`${datePattern}[^.\n]{0,60}(?:through|to|-|and)\s*${datePattern}[^.\n]{0,120}(?:therapy|pt|rehabilitation|physical therapy)`, 'i'));
  if (!match) return null;
  const first = parseDateOnly(match[1]);
  const second = parseDateOnly(match[2]);
  if (!first || !second) return null;
  const start = first.getTime() <= second.getTime() ? first : second;
  const end = first.getTime() <= second.getTime() ? second : first;
  const elapsedDays = daysBetween(start, end);
  return {
    start,
    end,
    elapsedDays,
    clinicalWeek: Math.floor(elapsedDays / 7) + 1,
  };
}

function hasDefensible12WeekTherapySpan(text: string): boolean {
  const explicit12WeekLanguage = /\b(?:12[- ]week|twelfth|12th)\b/i.test(text);
  const window = therapyWindow(text);
  return explicit12WeekLanguage || Boolean(window && window.elapsedDays >= 77 && window.clinicalWeek >= 12);
}

function isHospitalSiteOfCare(selectedCase: ScheduledCase, text: string): boolean {
  const site = normalize([selectedCase.facility_name, text].filter(Boolean).join(' '));
  return containsAny(site, ['hospital', 'st. elizabeth', 'medical center', 'opd', 'outpatient department']);
}

function hasSiteOfCareJustification(text: string): boolean {
  return /(ambulatory surgery center|asc)[^.\n]{0,160}(contraindicated|not appropriate|unsafe|not suitable)/i.test(text) ||
    /(hospital outpatient|hospital setting|st\.?\s*elizabeth)[^.\n]{0,200}(medically necessary|required|necessary)/i.test(text) ||
    /(cardiac|coronary|sleep apnea|airway|thromboembolic|post[- ]anesthetic)[^.\n]{0,200}(monitoring|risk|events|management)/i.test(text) ||
    hasAscExclusionSiteOfCareJustification(text);
}

function hasDiabetesRisk(text: string): boolean {
  return /\b(?:type 2 diabetes|diabetes|diabetic|dm2|t2dm)\b/i.test(text);
}

function hba1cEvidence(text: string, selectedCase: ScheduledCase): { value: number; date: Date | null; source: string; controlled: boolean } | null {
  const matches = Array.from(text.matchAll(/\b(?:hb\s*a1c|hemoglobin\s+a1c|a1c)\b[^.\n]{0,100}?(\d+(?:\.\d+)?)\s*%/gi));
  if (matches.length === 0) return null;
  const surgeryDate = parseDateOnly(selectedCase.scheduled_date);

  for (const match of matches) {
    const value = Number(match[1]);
    if (!Number.isFinite(value)) continue;
    const start = Math.max(0, match.index - 140);
    const end = Math.min(text.length, (match.index || 0) + match[0].length + 140);
    const surrounding = text.slice(start, end);
    const dateMatch = surrounding.match(/\b(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})\b/);
    const date = parseDateOnly(dateMatch?.[1]);
    const source = /(?:lab|laboratory|result|report)/i.test(surrounding) ? 'HbA1c laboratory report' : 'clinical text';
    const datedWithinWindow = !surgeryDate || !date || (daysBetween(date, surgeryDate) >= 0 && daysBetween(date, surgeryDate) <= 92);
    return {
      value,
      date,
      source,
      controlled: value < 8 && datedWithinWindow && source === 'HbA1c laboratory report',
    };
  }

  return null;
}

function hasBmiRisk(text: string): boolean {
  return /\b(?:bmi|body mass index|obesity|obese|body habitus)\b/i.test(text);
}

function bmiValue(text: string): number | null {
  const match = text.match(/\b(?:bmi|body mass index)\b\s*(?:is|=|of|:)?\s*(\d+(?:\.\d+)?)/i);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
}

function hasHighBmiDelayContraindication(text: string): boolean {
  return /(24[- ]week|24\s+weeks|twenty[- ]four\s+weeks|delay|waiting)[^.\n]{0,220}(contraindicated|arthrofibrosis|matur|calcif|permanent|revision|closed manipulation)/i.test(text) ||
    /(arthrofibrosis|matur|calcif|closed manipulation|revision)[^.\n]{0,220}(24[- ]week|24\s+weeks|delay|waiting)/i.test(text);
}

const SHOULDER_CPTS = ['29807', '23420', '23430', '23120', '29824', '29827', '29828'];

function hasShoulderFunctionalProof(text: string): boolean {
  return /(night pain|overhead|weakness|unable|difficulty|work|sleep|activities of daily living|adl|catching)/i.test(text);
}

function hasShoulderFormalTherapyProof(
  text: string,
  clinicalSeverityData?: ClinicalSeverityData | null,
): boolean {
  return hasShoulderFormalTherapyFailure(text, clinicalSeverityData);
}

function hasShoulderSiteOfCareJustification(
  text: string,
  selectedCase: ScheduledCase,
  clinicalSeverityData?: ClinicalSeverityData | null,
): boolean {
  if (!isHospitalSiteOfCare(selectedCase, text)) return true;
  return hasSiteOfCareJustification(text) ||
    hasAscExclusionSiteOfCareJustification(text, clinicalSeverityData) ||
    /(sleep apnea|osa|coronary|cardiac|airway|difficult airway|post[- ]anesthetic|hospital outpatient)[^.\n]{0,220}(monitoring|required|medically necessary|contraindicated|risk)/i.test(text);
}

function hasKneeArthroscopyProcedure(codes: string[], text: string): boolean {
  if (codes.includes('29880') || codes.includes('29881')) return true;
  const kneeContext = /\b(knee|meniscus|meniscal|mcmurray|joint[- ]line|tibiofemoral|femoral condyle)\b/i.test(text);
  return kneeContext && /\b(meniscectomy|meniscus tear|meniscal tear|locking|catching)\b/i.test(text);
}

function buildQuestion(params: {
  id: string;
  title: string;
  spokenPrompt: string;
  payerCriterion: string;
  missingFact: string;
  suggestedLanguage: string;
  answerPlaceholder: string;
  requirements: LiveValidationRequirement[];
  severity?: ClinicalQuestion['severity'];
  mode?: ConversationMode;
}): ClinicalQuestion {
  return {
    mode: params.mode || 'pre-auth',
    severity: params.severity || 'clinical',
    allowUseVerbatim: true,
    ...params,
  };
}

export function validateConversationAnswer(
  question: ClinicalQuestion,
  answer: string,
): ConversationValidationResult[] {
  const text = normalize(answer);
  return question.requirements.map(requirement => ({
    requirementId: requirement.id,
    label: requirement.label,
    satisfied: requirement.keywordGroups.every(group => containsAny(text, group)),
  }));
}

export function buildPreAuthProofPacket(params: {
  selectedCase: ScheduledCase;
  template: SurgeonTemplate;
  cptLines: CaseCptLine[];
  laterality: Laterality | null;
  clinicalText?: string | null;
  clinicalSeverityData?: ClinicalSeverityData | null;
  assistantNoteEnabled?: boolean;
}): SurgeonConsoleProofPacket {
  const {
    selectedCase,
    template,
    cptLines,
    laterality,
    clinicalText,
    clinicalSeverityData,
    assistantNoteEnabled,
  } = params;

  const rawText = getPacketText(selectedCase, clinicalText, clinicalSeverityData);
  const text = normalize(rawText);
  const cptCodes = cptLines.map(cptCode);
  const criteria: ProofCriterion[] = [];
  const questions: ClinicalQuestion[] = [];
  const revenueOpportunities: RevenueOpportunity[] = [];
  const primaryModifier = primaryLateralityModifier(laterality);

  criteria.push(criterion(
    'laterality',
    'CPT laterality is explicit',
    !!primaryModifier && hasModifier(cptLines, primaryModifier),
    primaryModifier
      ? `Primary procedure is aligned to ${primaryModifier}.`
      : 'Laterality must be explicit before the packet can be signed.',
    primaryModifier || undefined,
  ));

  criteria.push(criterion(
    'attachments',
    'Supporting packet attachments are present',
    (selectedCase.pre_loaded_docs || []).length >= 2,
    `${(selectedCase.pre_loaded_docs || []).length} document(s) are attached to the case.`,
    (selectedCase.pre_loaded_docs || []).map(doc => doc.type).join(', '),
  ));

  const isExtensorRepair =
    cptCodes.includes('27422') || cptCodes.includes('27385') || containsAny(text, ['extensor mechanism', 'quadriceps', 'vmo']);
  const isKneeManipulation =
    cptCodes.includes('27570') || cptCodes.includes('27603') || containsAny(text, ['manipulation', 'arthrofibrosis']);
  const isShoulderRepair =
    cptCodes.some(code => SHOULDER_CPTS.includes(code)) ||
    containsAny(text, ['shoulder', 'slap', 'rotator cuff', 'biceps tenodesis', 'distal clavicle']);
  const isCarpalTunnel =
    cptCodes.includes('64721') || containsAny(text, ['carpal tunnel', 'median neuropathy', 'transverse carpal ligament']);
  const isKneeArthroscopy =
    hasKneeArthroscopyProcedure(cptCodes, rawText);
  const isTotalKneeArthroplasty =
    cptCodes.includes('27447') ||
    (!isExtensorRepair && !isKneeManipulation && !isKneeArthroscopy && containsAny(text, ['tricompartmental osteoarthritis', 'bone-on-bone']));

  if (isExtensorRepair) {
    const traumatic = containsAny(text, ['fall', 'traumatic', 'acute injury', 'acute traumatic']);
    const imaging = containsAny(text, ['mri']) && containsAny(text, ['disruption', 'tear', 'quadriceps', 'vmo']);
    const functionLoss = containsAny(text, ['extensor lag', 'straight leg raise', 'weakness', 'difficulty performing straight']);
    const urgency = containsAny(text, ['urgent', 'delay', 'persistent extensor mechanism failure', 'risk']);
    const mrcQuantified = /\b(?:mrc|motor|strength|power)\b[^.\n]{0,80}\b[0-5]\s*\/\s*5\b/i.test(text) ||
      /\b[0-5]\s*\/\s*5\b[^.\n]{0,80}\b(?:mrc|motor|strength|power|straight[- ]leg raise)\b/i.test(text);

    criteria.push(criterion(
      'traumatic-mechanism',
      'Urgent traumatic mechanism documented',
      traumatic,
      traumatic
        ? 'Packet documents an acute traumatic mechanism.'
        : 'Payer bypass requires the acute traumatic mechanism to be explicit.',
      traumatic ? 'Urgent evaluation / packet text' : undefined,
    ));
    criteria.push(criterion(
      'imaging-confirmation',
      'Imaging confirms tendon disruption',
      imaging,
      imaging
        ? 'MRI evidence confirms quadriceps/VMO or extensor mechanism disruption.'
        : 'Payer proof needs objective imaging confirmation.',
      imaging ? 'MRI report' : undefined,
    ));
    criteria.push(criterion(
      'functional-deficit',
      'Functional deficit documented',
      functionLoss,
      functionLoss
        ? 'Functional loss is documented through extensor lag, weakness, or straight-leg raise limitation.'
        : 'Payer proof needs objective functional deficit.',
      functionLoss ? 'Urgent orthopedic evaluation' : undefined,
    ));
    criteria.push(criterion(
      'urgent-bypass',
      'Conservative therapy bypass is justified',
      traumatic && imaging && (functionLoss || urgency),
      'Traumatic extensor mechanism disruption can bypass routine conservative therapy when objective deficit and imaging are documented.',
      traumatic && imaging ? 'Urgent bypass pathway' : undefined,
    ));
    criteria.push(criterion(
      'mrc-strength-grade',
      'Motor weakness is quantified',
      mrcQuantified,
      mrcQuantified
        ? 'Motor deficit is quantified with an MRC/power grade.'
        : 'Payer-grade urgent bypass should quantify motor weakness, such as 0/5 or 1/5 straight-leg raise strength.',
      mrcQuantified ? 'Physical exam / surgeon attestation' : undefined,
    ));

    if (!traumatic || !imaging || !functionLoss || !urgency || !mrcQuantified) {
      questions.push(buildQuestion({
        id: 'extensor-urgent-bypass',
        title: 'Urgent repair attestation',
        spokenPrompt:
          'I found an extensor mechanism repair. To protect the authorization, can you confirm the injury was acute or traumatic, caused functional loss, and needed urgent repair?',
        payerCriterion:
          'Urgent traumatic extensor mechanism disruption with objective imaging and functional deficit.',
        missingFact:
          'The packet must explicitly connect trauma, quantified functional deficit, imaging confirmation, and urgency.',
        suggestedLanguage:
          'The patient sustained an acute traumatic left knee extensor mechanism disruption after recent total knee arthroplasty, with extensor lag and 0/5 active straight-leg raise strength on the MRC scale. MRI confirms quadriceps tendon and VMO disruption. Urgent repair is medically necessary to restore extensor mechanism function and delay would risk persistent functional deficit.',
        answerPlaceholder:
          'Dictate the acute mechanism, MRC strength grade, MRI finding, and why urgent repair is needed.',
        requirements: [
          { id: 'acute', label: 'Acute or traumatic mechanism', keywordGroups: [['acute', 'traumatic', 'fall']] },
          { id: 'function', label: 'Functional deficit', keywordGroups: [['extensor lag', 'straight leg raise', 'weakness']] },
          { id: 'mrc', label: 'Quantified MRC grade', keywordGroups: [['0/5', '1/5', 'mrc', 'strength', 'power']] },
          { id: 'imaging', label: 'Imaging confirmation', keywordGroups: [['mri', 'imaging'], ['disruption', 'tear', 'quadriceps', 'vmo']] },
          { id: 'urgency', label: 'Urgency rationale', keywordGroups: [['urgent', 'delay', 'risk', 'restore']] },
        ],
      }));
    }

    if (cptCodes.includes('27422')) {
      const realignmentProof = containsAny(text, [
        'patella alta',
        'patella baja',
        'patellar subluxation',
        'patella subluxation',
        'patellar malalignment',
        'maltracking',
        'dislocating patella',
        'realignment',
      ]);
      criteria.push(criterion(
        'patellar-realignment-proof',
        'Patellar realignment proof supports CPT 27422',
        realignmentProof,
        realignmentProof
          ? 'The packet contains patellar malalignment/realignment evidence needed for CPT 27422.'
          : 'CPT 27422 should not be authored for traumatic quadriceps/VMO repair unless patellar realignment pathology is explicit.',
        realignmentProof ? 'Imaging / surgeon interpretation' : undefined,
      ));
    }
  }

  if (isKneeManipulation) {
    const correctCode = cptCodes.includes('27570') && !cptCodes.includes('27603');
    const postTka = containsAny(text, ['total knee replacement', 'total knee arthroplasty', 'tka']);
    const arthrofibrosis = containsAny(text, ['arthrofibrosis', 'stiffness']);
    const structuredKneeMuaTherapyCourse = hasKneeMuaTherapyCourse(rawText, clinicalSeverityData);
    const structuredKneeMuaRom = hasKneeMuaRomEvidence(rawText, clinicalSeverityData);
    const ptPlateau = (
      containsAny(text, ['physical therapy', 'formal supervised therapy', 'pt progress']) &&
      containsAny(text, ['plateau', 'failed', 'limited additional expected gains'])
    ) || (structuredKneeMuaTherapyCourse && structuredKneeMuaRom);
    const rom = containsAny(text, ['range of motion', 'flexion', 'extension', '0 to 100', '95 to 100']) || structuredKneeMuaRom;
    const clearance = containsAny(text, ['medical clearance', 'no acute contraindication', 'cleared']);
    const functionalImpact =
      hasMuaFunctionalDeficit(rawText) ||
      hasKneeMuaFunctionalLimitation(rawText, clinicalSeverityData);
    const formalPtTimeline = hasFormalPtTimeline(rawText) || structuredKneeMuaTherapyCourse;
    const therapySpan = therapyWindow(rawText);
    const defensible12WeekSpan = hasDefensible12WeekTherapySpan(rawText) || structuredKneeMuaTherapyCourse;
    const staticRomTrendline = hasStaticRomTrendline(rawText) || structuredKneeMuaRom;
    const hospitalSiteRequired = isHospitalSiteOfCare(selectedCase, rawText);
    const siteOfCareJustified = !hospitalSiteRequired || hasSiteOfCareJustification(rawText);
    const diabetesRisk = hasDiabetesRisk(rawText);
    const hba1c = hba1cEvidence(rawText, selectedCase);
    const bmi = bmiValue(rawText);
    const bmiRisk = hasBmiRisk(rawText);
    const bmiClearsDynamicThreshold = !bmiRisk || (typeof bmi === 'number' && (bmi <= 40 || hasHighBmiDelayContraindication(rawText)));
    const globalAssessment = getMuaGlobalAssessment({ selectedCase, template, text: rawText });
    const has58Or78 = cptLines.some(line => cptCode(line) === '27570' && line.modifiers.some(modifier => ['58', '78'].includes(modifier)));

    criteria.push(criterion(
      'mua-cpt-code',
      'Knee manipulation CPT is correct',
      correctCode,
      correctCode
        ? 'CPT 27570 matches manipulation of the knee joint under general anesthesia.'
        : 'Knee MUA should be authored as CPT 27570, not CPT 27603.',
      correctCode ? 'Code cart' : undefined,
    ));
    criteria.push(criterion(
      'mua-global-period',
      'Global-period trap is neutralized',
      !!globalAssessment && (
        (globalAssessment.insideGlobal && has58Or78) ||
        (!globalAssessment.insideGlobal && !has58Or78)
      ),
      globalAssessment
        ? globalAssessment.insideGlobal
          ? `The MUA is ${globalAssessment.daysElapsed} days after the index TKA and needs the staged global-period modifier.`
          : `The MUA is ${globalAssessment.daysElapsed} days after the index TKA, outside the 90-day global period ending ${formatDate(globalAssessment.globalEndsOn)}.`
        : 'The packet must prove the prior TKA date and planned MUA date.',
      globalAssessment ? 'Scheduling + prior TKA date' : undefined,
    ));

    criteria.push(criterion(
      'post-tka-arthrofibrosis',
      'Post-TKA arthrofibrosis is documented',
      postTka && arthrofibrosis,
      'Manipulation authorization must show postoperative stiffness or arthrofibrosis after TKA.',
      postTka && arthrofibrosis ? 'Follow-up note / packet text' : undefined,
    ));
    criteria.push(criterion(
      'formal-pt-plateau',
      'Formal therapy failed with ROM plateau',
      ptPlateau && formalPtTimeline && defensible12WeekSpan,
      'Packet should prove a continuous supervised therapy course and plateau before manipulation.',
      ptPlateau ? 'PT progress note' : undefined,
    ));
    criteria.push(criterion(
      'carelon-therapy-duration',
      'Conservative therapy duration clears Carelon trap',
      formalPtTimeline && defensible12WeekSpan,
      therapySpan
        ? `Therapy ran ${therapySpan.elapsedDays} elapsed days and spans the ${therapySpan.clinicalWeek}th clinical week, supporting 12-week conservative-care wording.`
        : 'The packet must avoid "11 weeks" language and prove a 12-week or 12th-clinical-week therapy course.',
      therapySpan ? 'PT progress note dates' : undefined,
    ));
    criteria.push(criterion(
      'rom-proof',
      'ROM deficit is objective',
      rom && staticRomTrendline,
      'Objective flexion/extension values and repeated static measurements support medical necessity.',
      rom ? 'PT or orthopedic note' : undefined,
    ));
    criteria.push(criterion(
      'functional-impact',
      'ROM deficit creates functional impairment',
      functionalImpact,
      'Carelon-style review needs the gray-zone ROM deficit tied to actual stair or chair-rise impairment.',
      functionalImpact ? 'Surgeon clinical note / attestation' : undefined,
    ));
    criteria.push(criterion(
      'medical-clearance',
      'Medical clearance reviewed',
      clearance,
      'Clearance lowers avoidable authorization and scheduling risk for this packet.',
      clearance ? 'Medical clearance summary' : undefined,
    ));
    criteria.push(criterion(
      'site-of-care-hospital-necessity',
      'Hospital site of care is justified',
      siteOfCareJustified,
      hospitalSiteRequired
        ? 'Hospital outpatient MUA requires explicit ASC contraindication or hospital-level anesthesia/risk monitoring rationale.'
        : 'No hospital site-of-care redirection risk detected.',
      siteOfCareJustified && hospitalSiteRequired ? 'Medical clearance / surgeon attestation' : undefined,
    ));
    criteria.push({
      id: 'hba1c-lab-control',
      label: 'HbA1c lab proof is attached and controlled',
      status: !diabetesRisk ? 'not_applicable' : hba1c?.controlled ? 'satisfied' : 'missing',
      explanation: !diabetesRisk
        ? 'No diabetes risk was detected in the packet.'
        : hba1c
          ? `HbA1c ${hba1c.value}%${hba1c.date ? ` dated ${formatDate(hba1c.date)}` : ''} must be below 8% and supported by a lab report within the pre-op window.`
          : 'Diabetes is documented, but the packet lacks an attached HbA1c lab report showing control below 8% within the pre-op window.',
      source: hba1c?.controlled ? hba1c.source : undefined,
    });
    criteria.push({
      id: 'bmi-threshold-specificity',
      label: 'BMI threshold is explicit',
      status: !bmiRisk ? 'not_applicable' : bmiClearsDynamicThreshold ? 'satisfied' : 'missing',
      explanation: !bmiRisk
        ? 'No BMI-related risk term was detected in the packet.'
        : typeof bmi === 'number'
          ? bmi <= 40
            ? `BMI ${bmi.toFixed(1)} is below the BMI greater than 40 threshold that extends conservative therapy to 24 weeks.`
            : 'BMI is greater than 40, so the packet must state why waiting 24 weeks is clinically contraindicated for post-TKA arthrofibrosis.'
          : 'BMI is described vaguely. State the exact BMI value so Carelon cannot suspend the case for BMI-threshold review.',
      source: bmiClearsDynamicThreshold && typeof bmi === 'number' ? 'Medical clearance summary' : undefined,
    });

    if (!(postTka && arthrofibrosis) || !ptPlateau || !rom || !formalPtTimeline || !defensible12WeekSpan || !staticRomTrendline || !functionalImpact) {
      questions.push(buildQuestion({
        id: 'knee-manipulation-necessity',
        title: 'Knee manipulation medical necessity',
        spokenPrompt:
          'I found a knee manipulation request. Can you confirm the post-TKA stiffness, the continuous therapy course spanning the 12th clinical week, and how the range of motion is limiting function?',
        payerCriterion:
          'Post-arthroplasty arthrofibrosis with objective ROM plateau, defensible conservative-care duration, failed supervised therapy, and fixed functional impairment.',
        missingFact:
          'The packet must connect TKA history, stiffness or arthrofibrosis, therapy failure, objective ROM plateau, 12-week/12th-clinical-week duration, and stair/chair-rise functional impairment.',
        suggestedLanguage:
          'The patient has postoperative arthrofibrosis and stiffness after right total knee arthroplasty. She completed a continuous 77-day course of formal supervised in-person physical therapy from 03/02/2026 through 05/18/2026, spanning the 12th clinical week of conservative management, 2 to 3 times weekly. Repeated ROM measurements on 05/04/2026, 05/11/2026, and 05/18/2026 remained static at 95 to 100 degrees of flexion and 0 degrees extension. This fixed flexion limitation prevents normal reciprocal stair climbing and rising from a standard seated position without assistance. Further therapy alone is unlikely to restore functional motion, and CPT 27570-RT manipulation under anesthesia is medically necessary.',
        answerPlaceholder:
          'Dictate the TKA history, ROM trendline, 77-day/12th-week supervised therapy course, stair/chair limitation, and why manipulation is medically necessary.',
        requirements: [
          { id: 'tka', label: 'Prior TKA', keywordGroups: [['tka', 'total knee replacement', 'total knee arthroplasty']] },
          { id: 'stiffness', label: 'Arthrofibrosis or stiffness', keywordGroups: [['arthrofibrosis', 'stiffness']] },
          { id: 'therapy', label: 'Failed supervised therapy', keywordGroups: [['therapy', 'pt'], ['failed', 'plateau'], ['12-week', '12th', '77-day', '77 days']] },
          { id: 'rom', label: 'Objective ROM', keywordGroups: [['range of motion', 'rom', 'flexion', 'extension']] },
          { id: 'function', label: 'Functional impairment', keywordGroups: [['stairs', 'stair', 'rising', 'seated', 'chair']] },
        ],
      }));
    }
    if (!siteOfCareJustified) {
      questions.push(buildQuestion({
        id: 'hospital-site-of-care-necessity',
        title: 'Hospital site-of-care necessity',
        spokenPrompt:
          'Carelon may redirect this hospital case to an ASC. Can you document why the hospital outpatient setting is medically necessary for anesthesia safety?',
        payerCriterion:
          'Hospital outpatient MUA requires ASC contraindication or hospital-level monitoring rationale when the payer applies site-of-care redirection.',
        missingFact:
          'The packet must connect diabetes, coronary disease, sleep apnea, airway/cardiac monitoring, and ASC contraindication to St. Elizabeth Florence.',
        suggestedLanguage:
          'Due to the patient\'s cardiovascular and airway risk profile, including coronary artery disease, hypertension, type 2 diabetes, BMI 36.4, and sleep apnea requiring post-anesthetic airway and cardiopulmonary monitoring, an ambulatory surgery center is clinically contraindicated. The hospital outpatient setting at St. Elizabeth Florence is medically necessary to mitigate post-anesthetic thromboembolic, airway, and cardiac risk.',
        answerPlaceholder:
          'Dictate the specific comorbidities and why ASC care is unsafe for this anesthesia event.',
        requirements: [
          { id: 'asc', label: 'ASC contraindication', keywordGroups: [['asc', 'ambulatory surgery center'], ['contraindicated', 'unsafe', 'not appropriate']] },
          { id: 'risk', label: 'Hospital-level risk', keywordGroups: [['cardiac', 'coronary', 'airway', 'sleep apnea', 'thromboembolic'], ['monitoring', 'risk', 'management']] },
          { id: 'hospital', label: 'Hospital site requested', keywordGroups: [['hospital', 'st. elizabeth']] },
        ],
      }));
    }
  }

  if (isShoulderRepair) {
    const shoulderImaging = containsAny(text, ['mri']) && containsAny(text, ['slap', 'labrum', 'rotator cuff', 'supraspinatus', 'biceps', 'ac joint', 'distal clavicle']);
    const shoulderConservative = hasShoulderFormalTherapyProof(rawText, clinicalSeverityData);
    const shoulderFunction = hasShoulderFunctionalProof(rawText);
    const hasSlapBicepsConflict = cptCodes.includes('29807') && cptCodes.includes('23430');
    const hasDistalClavicleExcision = cptCodes.includes('23120') || cptCodes.includes('29824');
    const acJointDxMapped = !hasDistalClavicleExcision || /m19\.012|acromioclavicular|ac joint|distal clavicle|ac arthrosis/i.test(rawText);
    const siteOfCareJustified = hasShoulderSiteOfCareJustification(rawText, selectedCase, clinicalSeverityData);
    const secondaryLines = cptLines.filter(line => line.section === 'secondary');
    const xepsuSpecific = secondaryLines.length === 0 || secondaryLines.every(line =>
      line.modifiers.some(modifier => ['XE', 'XS', 'XP', 'XU'].includes(modifier)) ||
      (line.modifiers.includes('59') && /payer[- ]specific|denial defender|requires modifier 59|requires 59/i.test(rawText)),
    );
    const distinctStructures = secondaryLines.length === 0 || containsAny(text, [
      'distinct',
      'separate pain generator',
      'separate ac joint',
      'separate acromioclavicular',
      'separate structures',
      'labral fixation',
      'long-head biceps',
    ]);

    criteria.push(criterion(
      'shoulder-imaging-proof',
      'Shoulder MRI maps to requested CPTs',
      shoulderImaging,
      'Shoulder authorization must map each requested CPT to objective MRI or exam pathology.',
      shoulderImaging ? 'MRI / orthopedic evaluation' : undefined,
    ));
    criteria.push(criterion(
      'shoulder-conservative-care',
      'Shoulder conservative care failed',
      shoulderConservative,
      'Packet should prove failed therapy, medication/activity modification, injection when appropriate, or equivalent conservative care.',
      shoulderConservative ? 'PT / conservative care summary' : undefined,
    ));
    criteria.push(criterion(
      'shoulder-functional-loss',
      'Shoulder functional impairment is documented',
      shoulderFunction,
      'Night pain, overhead weakness, catching, or work/ADL limits should be explicit.',
      shoulderFunction ? 'Orthopedic evaluation' : undefined,
    ));
    criteria.push({
      id: 'shoulder-slap-biceps-conflict',
      label: 'SLAP repair and biceps tenodesis do not overlap',
      status: hasSlapBicepsConflict ? 'missing' : 'satisfied',
      explanation: hasSlapBicepsConflict
        ? 'CPT 29807 and CPT 23430 are both selected for the same shoulder. The surgeon must choose the medically accurate biceps-labral strategy before a payer packet can be generated.'
        : 'No same-shoulder SLAP repair/biceps tenodesis overlap was detected.',
      source: hasSlapBicepsConflict ? undefined : 'Code cart',
    });
    criteria.push({
      id: 'shoulder-ac-diagnosis-map',
      label: 'AC joint diagnosis maps to distal clavicle excision',
      status: acJointDxMapped ? 'satisfied' : 'missing',
      explanation: acJointDxMapped
        ? 'Distal clavicle excision is tied to AC joint arthrosis/pathology.'
        : 'Distal clavicle excision requires explicit AC joint diagnosis and imaging/exam support.',
      source: acJointDxMapped && hasDistalClavicleExcision ? 'MRI / X-ray / diagnosis map' : undefined,
    });
    criteria.push({
      id: 'shoulder-site-of-care',
      label: 'Shoulder site of care is justified',
      status: siteOfCareJustified ? 'satisfied' : 'needs_attestation',
      explanation: siteOfCareJustified
        ? 'Hospital site-of-care redirection risk is addressed or not applicable.'
        : 'Hospital shoulder surgery requires a patient-specific ASC contraindication or hospital-level monitoring rationale.',
      source: siteOfCareJustified ? 'Medical clearance / site-of-care attestation' : undefined,
    });
    criteria.push(criterion(
      'shoulder-xepsu-specificity',
      'Secondary shoulder CPTs use specific distinct-service logic',
      xepsuSpecific && distinctStructures,
      'Secondary shoulder CPTs need structure-specific XS/XE/XP/XU support. Modifier 59 is a payer-specific fallback only when Denial Defender or payer policy requires it.',
      xepsuSpecific && distinctStructures ? 'NCCI distinct-structure proof' : undefined,
    ));

    if (!shoulderImaging || !shoulderConservative || !shoulderFunction || !distinctStructures || hasSlapBicepsConflict || !acJointDxMapped || !siteOfCareJustified) {
      questions.push(buildQuestion({
        id: 'shoulder-medical-necessity',
        title: 'Shoulder procedure proof',
        spokenPrompt:
          'I found a shoulder authorization. Can you confirm the MRI findings, exact failed therapy dates, functional limits, site of care, and which structures are distinct for any secondary CPTs?',
        payerCriterion:
          'Shoulder repair authorization needs imaging-confirmed pathology, failed formal conservative care, functional impairment, defensible site of care, and distinct anatomic support for separately reportable secondary CPTs.',
        missingFact:
          'The packet must connect each requested shoulder CPT to a separate documented pathology, functional deficit, and payer-safe modifier/code-pair rule.',
        suggestedLanguage:
          'MRI dated 05/08/2026 confirms a full-thickness supraspinatus tear with 12 mm retraction, type 2 SLAP/labral pathology, biceps tendinopathy, and symptomatic AC joint arthrosis with distal clavicle edema. The patient completed 13 weeks of supervised in-person therapy from 02/10/2026 through 05/12/2026 with persistent night pain, overhead weakness, catching, and ADL limitation. Separately reportable secondary procedures are supported only when they address distinct structures such as rotator cuff/outlet and AC joint; XS is used for separate structure unless payer policy specifically requires Modifier 59.',
        answerPlaceholder:
          'Dictate the MRI pathology, therapy dates, functional deficit, site-of-care rationale, and distinct structures.',
        requirements: [
          { id: 'imaging', label: 'MRI pathology', keywordGroups: [['mri'], ['rotator cuff', 'slap', 'labrum', 'biceps', 'ac joint', 'distal clavicle']] },
          { id: 'conservative', label: 'Failed conservative care', keywordGroups: [['failed', 'persistent', 'despite'], ['therapy', 'injection', 'activity modification', 'home exercise'], ['week', 'from', 'through']] },
          { id: 'function', label: 'Functional loss', keywordGroups: [['night pain', 'overhead', 'weakness', 'catching', 'functional']] },
          { id: 'distinct', label: 'Distinct structures', keywordGroups: [['distinct', 'separate'], ['labrum', 'rotator cuff', 'biceps', 'ac joint']] },
        ],
      }));
    }
  }

  if (isCarpalTunnel) {
    const emgProof = containsAny(text, ['emg', 'ncs', 'nerve conduction']) &&
      containsAny(text, ['severe', 'median neuropathy', 'median nerve']);
    const medianSymptoms = containsAny(text, ['nocturnal', 'paresthesia', 'numbness', 'phalen', 'tinel', 'thenar', 'grip weakness']);
    const failedConservative = containsAny(text, ['splint', 'activity modification', 'injection', 'failed conservative', 'temporary relief']);

    criteria.push(criterion(
      'carpal-emg-proof',
      'EMG/NCS confirms severe median neuropathy',
      emgProof,
      'Carpal tunnel release proof should include electrodiagnostic confirmation when severe disease is the authorization basis.',
      emgProof ? 'EMG/NCS report' : undefined,
    ));
    criteria.push(criterion(
      'carpal-symptoms',
      'Median nerve symptoms are documented',
      medianSymptoms,
      'Nocturnal paresthesia, median-distribution numbness, provocative signs, or weakness should be explicit.',
      medianSymptoms ? 'Hand surgery evaluation' : undefined,
    ));
    criteria.push(criterion(
      'carpal-conservative-care',
      'Carpal tunnel conservative care failed',
      failedConservative,
      'Splinting, activity modification, medication, ergonomic changes, or injection failure should be documented.',
      failedConservative ? 'Conservative care summary' : undefined,
    ));
  }

  if (isTotalKneeArthroplasty) {
    const radiographicEndStage = containsAny(text, ['weight-bearing', 'x-ray', 'radiograph', 'kellgren']) &&
      containsAny(text, ['grade 4', 'bone-on-bone', 'joint-space collapse', 'tricompartmental']);
    const tkaFunction = containsAny(text, ['walking tolerance', 'less than one block', 'stair', 'night pain', 'cane', 'rising from a chair', 'antalgic']);
    const tkaConservative = containsAny(text, ['24 weeks', 'more than 24', 'conservative', 'therapy', 'bracing', 'injection', 'viscosupplement']);
    const bmi = bmiValue(rawText);
    const hba1c = hba1cEvidence(rawText, selectedCase);
    const biomarkerClearance = (!hasDiabetesRisk(rawText) || !!hba1c?.controlled) &&
      (typeof bmi === 'number') &&
      (!containsAny(text, ['cotinine', 'nicotine']) || containsAny(text, ['cotinine 4', 'cotinine is 4', 'no nicotine', 'negative']));
    const separateCapsularRelease = !cptCodes.includes('27435') || containsAny(text, [
      'surgeon review',
      'separately documented',
      'payer/ncci review',
      'fixed flexion contracture',
      'collateral ligament balancing',
    ]);

    criteria.push(criterion(
      'tka-radiographic-proof',
      'End-stage OA radiographic proof is attached',
      radiographicEndStage,
      'TKA authorization needs weight-bearing radiographic proof of end-stage disease.',
      radiographicEndStage ? 'Weight-bearing X-ray' : undefined,
    ));
    criteria.push(criterion(
      'tka-functional-loss',
      'TKA functional impairment is documented',
      tkaFunction,
      'Walking, stairs, chair rise, night pain, gait, or assistive-device limits should be explicit.',
      tkaFunction ? 'Orthopedic evaluation' : undefined,
    ));
    criteria.push(criterion(
      'tka-conservative-care',
      'TKA conservative care requirement is satisfied',
      tkaConservative,
      'Payer proof should show failed therapy, medications when tolerated, bracing, injections, and activity modification.',
      tkaConservative ? 'Conservative care summary' : undefined,
    ));
    criteria.push(criterion(
      'tka-risk-mitigation',
      'TKA BMI and biomarker risk is quantified',
      biomarkerClearance,
      'High-risk TKA packets must state exact BMI and attach clearance/lab risk mitigation rather than vague risk wording.',
      biomarkerClearance ? 'Medical clearance / labs' : undefined,
    ));
    criteria.push(criterion(
      'tka-secondary-code-review',
      'Secondary knee CPT is held to NCCI review',
      separateCapsularRelease,
      'Secondary capsular release or balancing work should not clear unless separate documentation and payer/NCCI review support it.',
      separateCapsularRelease ? 'Template metadata / op note proof' : undefined,
    ));
  }

  if (isKneeArthroscopy) {
    const meniscusMri = containsAny(text, ['mri']) && containsAny(text, ['medial meniscus', 'lateral meniscus', 'meniscus tear']);
    const mechanicalSymptoms = containsAny(text, ['locking', 'catching', 'mcmurray', 'joint-line', 'effusion']);
    const preservedJointSpace = containsAny(text, ['preserved', 'no bone-on-bone', 'without advanced', 'not end-stage']);
    const conservativeFailure = containsAny(text, ['conservative', 'therapy', 'activity modification', 'injection', 'anti-inflammatory']) &&
      containsAny(text, ['failed', 'persisted']);
    const bundledChondroplasty = !cptCodes.includes('29877') && containsAny(text, ['chondroplasty', 'bundled']);

    criteria.push(criterion(
      'meniscus-mri-proof',
      'MRI confirms meniscal tear pattern',
      meniscusMri,
      'Arthroscopy authorization should map MRI tear findings to the meniscectomy code.',
      meniscusMri ? 'MRI report' : undefined,
    ));
    criteria.push(criterion(
      'meniscus-mechanical-symptoms',
      'Mechanical symptoms are explicit',
      mechanicalSymptoms,
      'Payers deny degenerative arthroscopy when locking, catching, McMurray findings, effusion, or joint-line tenderness are absent.',
      mechanicalSymptoms ? 'Orthopedic evaluation' : undefined,
    ));
    criteria.push(criterion(
      'meniscus-oa-trap-neutralized',
      'End-stage OA arthroscopy trap is neutralized',
      preservedJointSpace,
      'Packet should prove the case is not arthroscopy for bone-on-bone OA.',
      preservedJointSpace ? 'X-ray report' : undefined,
    ));
    criteria.push(criterion(
      'meniscus-conservative-care',
      'Meniscus conservative care failed',
      conservativeFailure,
      'Conservative care failure should be stated before elective arthroscopy.',
      conservativeFailure ? 'Conservative care summary' : undefined,
    ));
    criteria.push(criterion(
      'meniscus-chondroplasty-bundled',
      'Bundled chondroplasty is not over-coded',
      bundledChondroplasty,
      'Chondroplasty should remain bundled unless payer-specific separate reporting is defensible.',
      bundledChondroplasty ? 'NCCI bundling guardrail' : undefined,
    ));
  }

  const legacy59 = cptLines.find(line => line.modifiers.includes('59'));
  if (legacy59) {
    revenueOpportunities.push({
      id: 'distinct-service-modifier-review',
      title: 'Confirm payer-specific 59 vs XS rule',
      reason:
        `CPT ${cptCode(legacy59)} carries Modifier 59. Keep it only when Denial Defender or payer policy specifically requires 59 for this code pair; otherwise use XS/XE/XP/XU when the distinct-service facts fit.`,
      suggestedLanguage:
        'The secondary procedure was distinct because it was performed on a separate anatomic structure from the primary service, supporting XS unless payer-specific policy requires Modifier 59 for this code pair.',
      estimatedImpactLabel: 'Reduces NCCI denial risk while preserving payer-specific exceptions',
      requiresSurgeonAttestation: false,
    });
  }

  const openCriteria = criteria.filter(item => item.status === 'needs_attestation' || item.status === 'missing');
  const hasStaffEvidenceGap = openCriteria.some(item => item.id === 'hba1c-lab-control' || item.id === 'bmi-threshold-specificity');
  const hasBlockingGap = openCriteria.some(item => [
    'shoulder-slap-biceps-conflict',
    'shoulder-ac-diagnosis-map',
    'mua-cpt-code',
    'mua-global-period',
    'hba1c-lab-control',
    'bmi-threshold-specificity',
  ].includes(item.id));
  const readinessStatus: SurgeonConsoleProofPacket['readinessStatus'] =
    hasBlockingGap
      ? 'blocked'
      : openCriteria.length === 0
      ? revenueOpportunities.length > 0 ? 'needs_surgeon' : 'ready'
      : hasStaffEvidenceGap ? 'needs_staff' : questions.length > 0 ? 'needs_surgeon' : 'needs_staff';

  return {
    mode: 'pre-auth',
    patientName: selectedCase.patient_name,
    procedureName: template.template_name || selectedCase.scheduled_procedure || 'Scheduled procedure',
    cptCodes,
    readinessStatus,
    summary:
      readinessStatus === 'ready'
        ? 'Denial Defender criteria are supported by the packet.'
        : 'The packet needs focused surgeon or staff input before it should be treated as enterprise-ready.',
    criteria,
    questions,
    revenueOpportunities,
  };
}

export function buildPostOpConversationPacket(params: {
  selectedCase: ScheduledCase;
  template: SurgeonTemplate;
  cptLines: CaseCptLine[];
  laterality: Laterality | null;
  transcript?: string | null;
  bmiValue?: number;
  highComplexity?: boolean;
}): SurgeonConsoleProofPacket {
  const { selectedCase, template, cptLines, laterality, transcript, bmiValue, highComplexity } = params;
  const text = normalize([transcript, template.template_name, selectedCase.scheduled_procedure].filter(Boolean).join('\n\n'));
  const cptCodes = cptLines.map(cptCode);
  const primaryModifier = primaryLateralityModifier(laterality);
  const criteria: ProofCriterion[] = [
    criterion(
      'authorized-match',
      'Performed work reconciles to authorized CPT universe',
      text.length === 0 || cptCodes.some(code => text.includes(code)) || containsAny(text, ['performed', 'repair', 'manipulation']),
      'The walkout interview verifies whether performed work matches the authorization.',
      cptCodes.join(', '),
    ),
    criterion(
      'laterality',
      'Laterality is carried into the op note',
      !!primaryModifier,
      primaryModifier ? `Authorized laterality is ${primaryModifier}.` : 'Laterality needs surgeon confirmation.',
      primaryModifier || undefined,
    ),
  ];

  const questions: ClinicalQuestion[] = [
    buildQuestion({
      id: 'post-op-match',
      mode: 'post-op',
      title: 'Procedure match-back',
      spokenPrompt:
        `I have ${selectedCase.patient_name}'s ${template.template_name} open. Did the performed procedure match the authorized CPTs ${cptCodes.join(', ') || 'on this case'}?`,
      payerCriterion:
        'The operative note must support the billed and authorized CPT universe.',
      missingFact:
        'Performed procedure match, variance, and any intra-op discovery must be explicit.',
      suggestedLanguage:
        `The performed procedure matched the authorized procedure, including ${cptCodes.join(', ') || 'the authorized CPT bundle'}${primaryModifier ? ` with ${primaryModifier} laterality` : ''}. No unplanned separately billable procedure was performed unless separately documented below.`,
      answerPlaceholder:
        'Dictate whether the procedure matched the authorization and mention any variance or added work.',
      requirements: [
        { id: 'match', label: 'Match or variance stated', keywordGroups: [['matched', 'same', 'variance', 'additional', 'not performed']] },
        { id: 'procedure', label: 'Procedure performed', keywordGroups: [['performed', 'repair', 'manipulation', 'arthroscopy', 'procedure']] },
      ],
    }),
    buildQuestion({
      id: 'post-op-complications',
      mode: 'post-op',
      title: 'Complications and disposition',
      spokenPrompt:
        'Were there any complications, and was the patient stable at the end of the case?',
      payerCriterion:
        'The final operative note should include complications and patient disposition.',
      missingFact:
        'Complication status and end-of-case condition.',
      suggestedLanguage:
        'There were no intraoperative complications. The patient tolerated the procedure well and was transferred from the operating room in stable condition.',
      answerPlaceholder:
        'Dictate complications, if any, and the patient condition at transfer.',
      requirements: [
        { id: 'complications', label: 'Complication status', keywordGroups: [['no complications', 'complication', 'without complication']] },
        { id: 'stable', label: 'Disposition', keywordGroups: [['stable', 'tolerated', 'transferred']] },
      ],
    }),
  ];

  const revenueOpportunities: RevenueOpportunity[] = [];
  if ((highComplexity || (typeof bmiValue === 'number' && bmiValue >= 40)) && !hasModifier(cptLines, '22')) {
    revenueOpportunities.push({
      id: 'post-op-modifier-22',
      title: 'Document increased work if clinically true',
      reason:
        'High BMI or complexity is present. The op note needs a specific increased-work statement before Modifier 22 can be supported.',
      suggestedLanguage:
        `Due to BMI ${bmiValue?.toFixed(1) || 'above 40'} and body habitus, exposure, positioning, visualization, and closure required substantially increased work and operative time compared with a standard case.`,
      estimatedImpactLabel: 'Protects legitimate Modifier 22 recovery',
      requiresSurgeonAttestation: true,
    });
  }

  return {
    mode: 'post-op',
    patientName: selectedCase.patient_name,
    procedureName: template.template_name,
    cptCodes,
    readinessStatus: revenueOpportunities.length > 0 ? 'needs_surgeon' : 'ready',
    summary:
      'Walkout interview keeps the surgeon in a natural conversation while the system reconciles authorization, documentation, and revenue integrity.',
    criteria,
    questions,
    revenueOpportunities,
  };
}

export function revenueOpportunityToQuestion(
  opportunity: RevenueOpportunity,
  mode: ConversationMode,
): ClinicalQuestion {
  return buildQuestion({
    id: opportunity.id,
    mode,
    title: opportunity.title,
    spokenPrompt:
      `${opportunity.reason} If clinically true, do you want to document it now?`,
    payerCriterion: opportunity.estimatedImpactLabel,
    missingFact: opportunity.reason,
    suggestedLanguage: opportunity.suggestedLanguage,
    answerPlaceholder:
      'Dictate the clinical facts that support this revenue opportunity, or use the suggested language if it is accurate.',
    severity: 'revenue',
    requirements: [
      { id: 'clinical-support', label: 'Clinical support stated', keywordGroups: [['required', 'necessary', 'increased', 'substantial', 'distinct', 'separate', 'assistant']] },
    ],
  });
}

export function formatProofPacketForNarrative(
  packet: SurgeonConsoleProofPacket,
  surgeonStatements: string[] = [],
): string {
  const criteriaLines = packet.criteria.map(item => {
    const status = item.status === 'satisfied' ? 'SATISFIED' : item.status === 'not_applicable' ? 'N/A' : 'NEEDS ATTESTATION';
    return `- ${item.label}: ${status}. ${item.explanation}${item.source ? ` Source: ${item.source}.` : ''}`;
  });

  const statementLines = surgeonStatements.length > 0
    ? ['Surgeon Attestations:', ...surgeonStatements.map(statement => `- ${statement}`)]
    : [];

  return [
    'Denial Defender Proof Packet:',
    `Readiness: ${packet.readinessStatus.toUpperCase()}`,
    `Summary: ${packet.summary}`,
    'Criteria Proof:',
    ...criteriaLines,
    ...statementLines,
  ].join('\n');
}
